import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/utils/cn'

const selectTriggerVariants = cva(
  'flex w-full items-center justify-between px-5 text-body-md text-primary border-input transition-200',
  {
    variants: {
      size: {
        sm: 'h-6 px-2 rounded-2',
        md: 'h-8 px-3 rounded-2.5',
        lg: 'h-10 px-4 rounded-3',
        lgx: 'h-12 px-5 rounded-3.5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const Select = SelectPrimitive.Root
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value
const SelectLabel = SelectPrimitive.Label
const SelectSeparator = SelectPrimitive.Separator

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & VariantProps<typeof selectTriggerVariants>
>(({ className, size = 'md', disabled, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(selectTriggerVariants({ size }), disabled && 'cursor-not-allowed opacity-60', className)}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon className='text-brand-primary'>
      <iconpark-icon name="outline-down-2px" size="14" fill='currentColor' />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, position = 'popper', sideOffset = 4, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'z-50 w-[var(--radix-select-trigger-width)] overflow-hidden p-.5 bg-surface bor-2 rounded-3 shadow-lg',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
        className
      )}
      position={position}
      sideOffset={sideOffset}
      {...props}
    />
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex items-center w-full h-10 px-3 text-body-md text-primary rounded-2.5 transition-200 cursor-pointer select-none outline-none',
      'hover:bg-overlay-1 focus:bg-overlay-1',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className
    )}
    {...props}
  >
      <span className="absolute right-3 flex size-3.5 items-center justify-center color-brand-primary">
        <SelectPrimitive.ItemIndicator>
          <iconpark-icon name="outline-check-2px" size="14" />
        </SelectPrimitive.ItemIndicator>
      </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
}